import React from 'react';

// Modern styled table components for ReactMarkdown that match PDF output
const TableComponents = {
  table: ({node, ...props}) => (
    <div style={{
      overflowX: 'auto',
      marginBottom: '1.5rem',
      marginTop: '1.5rem',
      borderRadius: '8px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
    }}>
      <table 
        className="react-markdown-table"
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontFamily: '"Roboto", "Arial", sans-serif',
          fontSize: '0.875rem',
          borderRadius: '8px',
          overflow: 'hidden',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }} 
        {...props} 
      />
    </div>
  ),
  
  thead: ({node, ...props}) => (
    <thead 
      className="table-header"
      style={{
        backgroundColor: '#1D2B3F', // Dark blue header matching PDF
        color: 'white'
      }} 
      {...props} 
    />
  ),
  
  tbody: ({node, ...props}) => (
    <tbody {...props} />
  ),
  
  th: ({node, ...props}) => (
    <th 
      style={{
        padding: '12px 16px',
        textAlign: 'left',
        fontWeight: '600',
        backgroundColor: '#1D2B3F', // Explicitly set background color for header
        color: '#F5821F', // Orange color to match PDF
        borderBottom: '2px solid rgba(245, 130, 31, 0.2)',
        whiteSpace: 'nowrap',
        position: 'relative'
      }} 
      {...props} 
    />
  ),
  
  td: ({node, ...props}) => (
    <td style={{
      padding: '10px 16px',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      color: '#e0e0e0', // Light gray text to match PDF
      verticalAlign: 'top'
    }} {...props} />
  ),
  
  tr: ({node, isHeader, ...props}) => {
    // Don't apply hover effects to header rows
    if (isHeader) {
      return (
        <tr 
          style={{
            backgroundColor: '#1D2B3F',
            color: 'white'
          }} 
          {...props}
        />
      );
    }
    
    // For body rows, add hover effect
    return (
      <tr 
        style={{
          backgroundColor: props.isOdd ? 'rgba(45, 45, 45, 0.5)' : 'rgba(37, 37, 37, 0.5)', // Match PDF even/odd row colors
          color: '#e0e0e0',
          transition: 'background-color 0.2s ease'
        }} 
        onMouseEnter={(e) => {e.currentTarget.style.backgroundColor = 'rgba(245, 130, 31, 0.1)'}}
        onMouseLeave={(e) => {
          // Find position in table to determine if odd or even row
          const rowIndex = Array.from(e.currentTarget.parentNode.children).indexOf(e.currentTarget);
          e.currentTarget.style.backgroundColor = rowIndex % 2 === 0 ? 'rgba(37, 37, 37, 0.5)' : 'rgba(45, 45, 45, 0.5)';
        }}
        {...props}
      />
    );
  },
  
  // Additional components for other markdown elements to match PDF styling
  h1: ({node, ...props}) => (
    <h1 style={{
      color: '#F5821F', // Orange to match PDF
      fontWeight: 600, 
      marginTop: '1.5rem', 
      marginBottom: '1rem',
      borderBottom: '1px solid rgba(245, 130, 31, 0.2)',
      paddingBottom: '0.5rem'
    }} {...props} />
  ),
  
  h2: ({node, ...props}) => (
    <h2 style={{
      color: '#e0e0e0', // Light gray to match PDF
      fontWeight: 600, 
      marginTop: '1.25rem', 
      marginBottom: '0.75rem'
    }} {...props} />
  ),
  
  h3: ({node, ...props}) => (
    <h3 style={{
      color: '#e0e0e0', // Light gray to match PDF
      fontWeight: 600, 
      marginTop: '1rem', 
      marginBottom: '0.5rem'
    }} {...props} />
  ),
  
  code: ({node, inline, className, children, ...props}) => {
    const match = /language-(\w+)/.exec(className || '');
    
    return !inline ? (
      <pre style={{
        backgroundColor: '#2c3e50', // Darker blue to match PDF code blocks
        color: '#f8f8f2', // Light blue/gray for code text 
        padding: '1rem', 
        borderRadius: '8px',
        overflow: 'auto',
        fontSize: '0.875rem',
        fontFamily: 'monospace',
        marginBottom: '1rem',
        position: 'relative',
        border: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        {match && (
          <div style={{
            position: 'absolute',
            top: '5px',
            right: '10px',
            fontSize: '0.75rem',
            color: 'rgba(255,255,255,0.6)',
            textTransform: 'uppercase'
          }}>
            {match[1]}
          </div>
        )}
        <code className={className} {...props}>
          {children}
        </code>
      </pre>
    ) : (
      <code 
        style={{ 
          backgroundColor: 'rgba(44, 62, 80, 0.7)', // Matching code block color
          padding: '2px 4px', 
          borderRadius: '4px', 
          fontFamily: 'monospace',
          color: '#f8f8f2' // Light blue/gray for code text
        }} 
        {...props}
      >
        {children}
      </code>
    );
  },
  
  blockquote: ({node, ...props}) => (
    <blockquote style={{
      borderLeft: '4px solid #F5821F', // Orange border to match theme
      paddingLeft: '1rem', 
      paddingTop: '0.25rem',
      paddingBottom: '0.25rem',
      color: '#e0e0e0',
      margin: '1rem 0',
      backgroundColor: 'rgba(45, 45, 45, 0.3)'
    }} {...props} />
  ),
  
  img: ({node, ...props}) => (
    <img 
      style={{ 
        maxWidth: '100%', 
        height: 'auto',
        borderRadius: '8px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        margin: '1.5rem auto',
        display: 'block' // Center images like in PDF
      }}
      {...props}
    />
  ),
  
  ul: ({node, ...props}) => (
    <ul style={{
      paddingLeft: '2rem',
      marginBottom: '1rem',
      color: '#e0e0e0' // Light gray to match PDF
    }} {...props} />
  ),
  
  ol: ({node, ...props}) => (
    <ol style={{
      paddingLeft: '2rem',
      marginBottom: '1rem',
      color: '#e0e0e0' // Light gray to match PDF
    }} {...props} />
  ),
  
  li: ({node, ...props}) => (
    <li style={{
      marginBottom: '0.5rem',
      color: '#e0e0e0' // Light gray to match PDF
    }} {...props} />
  ),
  
  hr: ({node, ...props}) => (
    <hr style={{
      border: 'none',
      height: '1px',
      backgroundColor: 'rgba(245, 130, 31, 0.2)', // Light orange to match theme
      margin: '1.5rem 0'
    }} {...props} />
  ),
  
  a: ({node, ...props}) => (
    <a style={{
      color: '#F5821F', // Orange to match theme and PDF
      textDecoration: 'none',
      borderBottom: '1px dotted #F5821F',
      transition: 'color 0.2s ease, border-bottom-color 0.2s ease'
    }} 
      onMouseEnter={(e) => {
        e.currentTarget.style.color = '#ff9d45';
        e.currentTarget.style.borderBottomColor = '#ff9d45';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = '#F5821F';
        e.currentTarget.style.borderBottomColor = '#F5821F';
      }}
      {...props} 
    />
  ),
  
  p: ({node, ...props}) => (
    <p style={{
      marginBottom: '1rem',
      lineHeight: '1.6',
      color: '#e0e0e0' // Light gray to match PDF
    }} {...props} />
  ),
  
  strong: ({node, ...props}) => (
    <strong style={{
      fontWeight: '600',
      color: '#ffffff' // White for bold to match PDF
    }} {...props} />
  ),
  
  em: ({node, ...props}) => (
    <em style={{
      fontStyle: 'italic',
      color: '#e0e0e0' // Light gray to match PDF
    }} {...props} />
  )
};

// Add helper function to initialize row styling on first render
// This ensures zebra striping works even without hover events
const initializeRowStyles = () => {
  setTimeout(() => {
    const tables = document.querySelectorAll('.react-markdown-table');
    tables.forEach(table => {
      const rows = table.querySelectorAll('tbody tr');
      rows.forEach((row, index) => {
        row.style.backgroundColor = index % 2 === 0 
          ? 'rgba(37, 37, 37, 0.5)'  // Even rows - match PDF
          : 'rgba(45, 45, 45, 0.5)'; // Odd rows - match PDF
        row.style.color = '#e0e0e0';
      });
    });
  }, 0);
};

// Add some CSS to document head to ensure consistent styling
const addTableCss = () => {
  if (!document.getElementById('table-components-css')) {
    const style = document.createElement('style');
    style.id = 'table-components-css';
    style.textContent = `
      /* Making sure table headers are clearly styled with high specificity */
      .react-markdown-table thead,
      .react-markdown-table thead tr,
      .react-markdown-table thead th,
      .table-header,
      .table-header th,
      table.react-markdown-table > thead > tr > th {
        background-color: #1D2B3F !important;
        color: #F5821F !important;
        font-weight: bold !important;
      }
      
      /* Prevent hover effects on header rows */
      .react-markdown-table thead tr:hover,
      .table-header:hover {
        background-color: #1D2B3F !important;
      }
      
      /* Body rows styling - matching PDF colors */
      .react-markdown-table tbody tr:nth-of-type(odd) {
        background-color: rgba(45, 45, 45, 0.5);
        color: #e0e0e0;
      }
      .react-markdown-table tbody tr:nth-of-type(even) {
        background-color: rgba(37, 37, 37, 0.5);
        color: #e0e0e0;
      }
      .react-markdown-table tbody tr:hover {
        background-color: rgba(245, 130, 31, 0.1) !important;
      }
      .react-markdown-table td {
        color: #e0e0e0;
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      }
      
      /* Table header dividers */
      .react-markdown-table thead th:not(:last-child)::after {
        content: "";
        position: absolute;
        right: 0;
        top: 20%;
        height: 60%;
        width: 1px;
        background-color: rgba(255, 255, 255, 0.2);
      }
      
      /* Text shadow for better readability */
      .react-markdown-table thead th {
        text-shadow: 0 1px 1px rgba(0, 0, 0, 0.5);
      }
      
      /* Code blocks styling to match PDF */
      pre {
        background-color: #2c3e50 !important;
        color: #f8f8f2 !important;
      }
      code {
        background-color: rgba(44, 62, 80, 0.7) !important;
        color: #f8f8f2 !important;
      }
    `;
    document.head.appendChild(style);
  }
  initializeRowStyles();
};

// Call this function when component mounts
if (typeof window !== 'undefined') {
  addTableCss();
}

export default TableComponents;